/**
 * Small pure helpers used by the Session Detail page and its sub-components.
 *
 * Lifecycle-driven action visibility, chip colour resolution, charges totals.
 * Kept out of components so they're unit-testable later without rendering.
 */

import type {
  CheckInLifecycleStatus,
  CheckInPaymentState,
  CheckInSession,
  CheckInIntakeType,
  CheckInTicketGrouping,
} from '@techgitter/contracts';

export type ChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

export const LIFECYCLE_LABEL: Record<string, string> = {
  draft_created: 'Draft',
  awaiting_customer_intake: 'Awaiting Intake',
  intake_in_progress: 'Intake In Progress',
  terms_accepted: 'Terms Accepted',
  creating_jobs: 'Creating Jobs',
  job_creation_failed: 'Job Creation Failed',
  awaiting_payment: 'Awaiting Payment',
  awaiting_bank_transfer: 'Awaiting Bank Transfer',
  payment_failed: 'Payment Failed',
  paid: 'Paid',
  awaiting_physical_dropoff: 'Awaiting Drop-off',
  inspection_in_progress: 'Inspection',
  checked_in: 'Checked In',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const LIFECYCLE_COLOR: Record<string, ChipColor> = {
  draft_created: 'default',
  awaiting_customer_intake: 'info',
  intake_in_progress: 'info',
  terms_accepted: 'info',
  creating_jobs: 'info',
  job_creation_failed: 'error',
  awaiting_payment: 'warning',
  awaiting_bank_transfer: 'warning',
  payment_failed: 'error',
  paid: 'success',
  awaiting_physical_dropoff: 'warning',
  inspection_in_progress: 'info',
  checked_in: 'success',
  expired: 'default',
  cancelled: 'default',
};

export function lifecycleLabel(status: string | null | undefined): string {
  if (!status) return '';
  return LIFECYCLE_LABEL[status] ?? status;
}

export function lifecycleChipColor(status: string | null | undefined): ChipColor {
  if (!status) return 'default';
  return LIFECYCLE_COLOR[status] ?? 'default';
}

export const PAYMENT_LABEL: Record<string, string> = {
  not_required: 'No payment needed',
  awaiting_payment: 'Awaiting payment',
  awaiting_bank_transfer: 'Awaiting bank transfer',
  paid: 'Paid',
  paid_overridden: 'Paid (overridden)',
  deferred: 'Invoice sent — deferred',
  deferred_awaiting_payment: 'Invoice sent — awaiting payment',
  failed: 'Payment failed',
};

const PAYMENT_COLOR: Record<string, ChipColor> = {
  not_required: 'default',
  awaiting_payment: 'warning',
  awaiting_bank_transfer: 'warning',
  paid: 'success',
  paid_overridden: 'success',
  deferred: 'info',
  deferred_awaiting_payment: 'warning',
  failed: 'error',
};

export function paymentLabel(state: string | null | undefined): string {
  if (!state) return '';
  return PAYMENT_LABEL[state] ?? state;
}

export function paymentChipColor(state: string | null | undefined): ChipColor {
  if (!state) return 'default';
  return PAYMENT_COLOR[state] ?? 'default';
}

/**
 * Whether the payment chip would only repeat the lifecycle chip beside it.
 *
 * Four lifecycle statuses *are* payment statuses, and the header renders both
 * chips — so an awaiting-payment session showed "Awaiting Payment" followed by
 * "Awaiting payment", and a paid one "Paid" twice. The lifecycle chip is the
 * one that stays (it is the session's position, and it is there in every
 * state), so the payment chip is dropped when it adds nothing.
 *
 * `paid_overridden` on a `paid` lifecycle is NOT a repeat — "Paid
 * (overridden)" says something the lifecycle cannot.
 */
const PAYMENT_RESTATED_BY_LIFECYCLE: Record<string, string> = {
  awaiting_payment: 'awaiting_payment',
  awaiting_bank_transfer: 'awaiting_bank_transfer',
  payment_failed: 'failed',
  paid: 'paid',
};

export function paymentChipRestatesLifecycle(
  lifecycleStatus: string | null | undefined,
  paymentState: string | null | undefined,
): boolean {
  if (!lifecycleStatus || !paymentState) return false;
  return PAYMENT_RESTATED_BY_LIFECYCLE[lifecycleStatus] === paymentState;
}

export function intakeTypeLabel(intake: CheckInIntakeType | string | null | undefined): string {
  if (intake === 'in_store') return 'In-Store';
  if (intake === 'online') return 'Online';
  if (intake === 'express') return 'Express';
  if (intake === 'in_store_return') return 'In-store (Return)';
  if (intake === 'in_store_dropoff') return 'In-store (Drop Off)';
  return String(intake ?? '');
}

// ---------------------------------------------------------------------------
// Lifecycle-driven action visibility — single source of truth shared by the
// action bar (sticky bottom on mobile, header on desktop) and the page itself
// when it needs to know which sections render their inline buttons.
// ---------------------------------------------------------------------------

export interface SessionActionFlags {
  canMarkPaid: boolean;
  canDeferPayment: boolean;
  canRetry: boolean;
  canComplete: boolean;
  /** Set when canComplete is false purely because ticket assignment isn't resolved.
   *  UI uses this to show "Resolve ticket assignment for all devices" on the
   *  disabled Complete button rather than the generic disabled tooltip. */
  ticketAssignmentBlocking: boolean;
  canCancel: boolean;
  canRecover: boolean;
  isTerminal: boolean;
}

/** A device is resolved for completion once it carries a RepairShopr ticket
 *  id. Mirrors the server's rule in `complete_session`.
 *
 *  **Express is the one exception**, and `intakeType` is why this takes one:
 *  express enqueues its orchestration FROM completion, so its tickets
 *  legitimately do not exist yet. Everywhere else the orchestration has
 *  already run, so a device with no ticket is one nothing is left to create
 *  one for — which is how a session reached `checked_in` with no RepairShopr
 *  ticket. Reception raises those with Create Tickets before completing.
 *
 *  This used to also accept `ticketAssignmentMode === 'create_new'` as an
 *  answer. That column is retired with the per-device picker: it was a promise
 *  rather than a fact, and the promise is what went unkept. */
export function isDraftJobTicketResolved(
  draftJob: { repairShoprTicketId?: string | null },
  intakeType?: string | null,
): boolean {
  if (draftJob.repairShoprTicketId) return true;
  return intakeType === 'express';
}

// Lifecycle states where Mark Paid / Defer / Not Required are surfaced.
//
// Strictly speaking the workflow moves through draft_created → … →
// awaiting_payment before payment is collected. Until sm4rt-orchestrator's
// check-in workflow lands, terms-acceptance can't actually advance the
// session past `creating_jobs`, which leaves new sessions stuck pre-payment.
//
// To unblock staff testing for now we surface Mark Paid from every
// pre-paid, non-terminal state. Re-tighten this set once the orchestrator
// is doing its job.
const MARK_PAID_FROM = new Set<CheckInLifecycleStatus | string>([
  'draft_created',
  'awaiting_customer_intake',
  'intake_in_progress',
  'terms_accepted',
  'creating_jobs',
  'job_creation_failed',
  'awaiting_payment',
  'awaiting_bank_transfer',
  'payment_failed',
]);
const CANCELLABLE_FROM = new Set<string>([
  'draft_created',
  'awaiting_customer_intake',
  'intake_in_progress',
  'terms_accepted',
  'creating_jobs',
  'job_creation_failed',
  'awaiting_payment',
  'awaiting_bank_transfer',
  'payment_failed',
  'awaiting_physical_dropoff',
  'expired',
]);

export function deriveActionFlags(session: CheckInSession): SessionActionFlags {
  const lifecycle = (session.lifecycleStatus ?? '') as string;
  const payment = (session.paymentState ?? '') as CheckInPaymentState;
  const draftJobs = session.draftJobs ?? [];
  const allInspected = draftJobs.every(j => j.inspectionStatus === 'complete');
  const allTicketsResolved = draftJobs.length > 0
    && draftJobs.every(job => isDraftJobTicketResolved(job, session.intakeType));
  const isTerminal = lifecycle === 'checked_in' || lifecycle === 'cancelled';
  const lifecycleAllowsComplete = lifecycle === 'inspection_in_progress' && allInspected;
  return {
    canMarkPaid: MARK_PAID_FROM.has(lifecycle),
    canDeferPayment: MARK_PAID_FROM.has(lifecycle),
    canRetry: !!session.retryAvailable,
    canComplete: lifecycleAllowsComplete && allTicketsResolved,
    ticketAssignmentBlocking: lifecycleAllowsComplete && !allTicketsResolved,
    canCancel: CANCELLABLE_FROM.has(lifecycle),
    canRecover: lifecycle === 'expired',
    isTerminal,
    // Payment state still referenced (e.g. charges-locked indicator)
    // — kept on flags so consumers don't pull it out themselves
    ...{ _payment: payment } as unknown as object,
  };
}

// ---------------------------------------------------------------------------
// The ticket a device became — how it is labelled and where it links
// ---------------------------------------------------------------------------

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

export interface DeviceTicketRef {
  /** What the chip reads: the RS ticket number, or the id when that's all we have. */
  label: string;
  /** The sm4rt ticket page, or null when we can't address it (see below). */
  sm4rtHref: string | null;
  /** The same ticket in RepairShopr. Always available — the id is what RS takes. */
  rsHref: string;
}

/**
 * A RepairShopr ticket carries two identifiers: an internal `id` and the
 * human-facing `number`. The check-in pipeline historically stored only the id,
 * so the device chips read "RS #90241877" — a number nobody can quote, with no
 * route to the ticket inside sm4rt.
 *
 * **The sm4rt ticket page is addressed by number only** (`/ticket/:number` →
 * `getWorkItemByNumber`), so a device whose draft job has no stored number
 * cannot link there at all; it falls back to the id and RepairShopr. The number
 * arrives from `cr001_repairshopr_ticket_number`, which sm4rt-api and
 * sm4rt-orchestrator read and write only when `CHECK_IN_TICKET_NUMBER_COLUMN`
 * is on — so a device checked in before that was enabled keeps the fallback
 * for good, and there is no way to resolve one from the other in the browser.
 *
 * Returns null when the device has no ticket yet, which is a state the caller
 * renders differently (unresolved / create-on-complete), not a missing link.
 */
export function deviceTicketRef(draftJob: {
  repairShoprTicketId?: string | null;
  repairShoprTicketNumber?: string | null;
}): DeviceTicketRef | null {
  const id = draftJob.repairShoprTicketId;
  if (!id) return null;
  const number = draftJob.repairShoprTicketNumber;
  return {
    label: number ?? id,
    sm4rtHref: number ? `/ticket/${number}` : null,
    rsHref: `${REPAIRSHOPR_BASE_URL}/tickets/${id}`,
  };
}

/**
 * The one RepairShopr ticket this whole check-in resolves to, or null.
 *
 * Returns a ref only when every ticketed device points at the **same** ticket,
 * which is the single-job grouping and a drop-off bound to the job it extends.
 * On a `job_per_device` check-in there are several, and a single session-level
 * "Job" link would name one of them as if it were the job — so it stays silent
 * and the per-device rows remain the only answer.
 *
 * Deliberately derived from the devices rather than read off the session: the
 * session has no ticket column, and the devices are where the orchestrator
 * stamps both identifiers.
 */
export function sessionTicketRef(session: CheckInSession | null): DeviceTicketRef | null {
  const ticketed = (session?.draftJobs ?? []).filter(j => j.repairShoprTicketId);
  if (ticketed.length === 0) return null;
  const distinct = new Set(ticketed.map(j => String(j.repairShoprTicketId)));
  if (distinct.size !== 1) return null;
  // Prefer a device that also carries the number, so the in-app link survives
  // one unbackfilled row sitting beside backfilled ones.
  const withNumber = ticketed.find(j => j.repairShoprTicketNumber) ?? ticketed[0];
  return deviceTicketRef(withNumber);
}

/**
 * The session's invoice, as a link.
 *
 * Prefers sm4rt's `/invoice/{number}`. `repairShoprInvoiceNumber` is resolved
 * server-side from the stored RS id on the session **detail** (the list
 * projection never carries it), and is null for roughly the first five minutes
 * while the rs2dv sync lands the invoice row — so the RepairShopr link, which
 * takes the id, is the fallback. Same shape of degradation `deviceTicketRef`
 * applies to a ticket with no stored number.
 *
 * The id is **never** the label: it looks like a number a person could quote
 * and is not one. It rides in `title` for anyone who needs to search RS.
 */
export function sessionInvoiceRef(session: CheckInSession | null): {
  label: string;
  sm4rtHref: string | null;
  rsHref: string;
  title: string;
} | null {
  const id = session?.repairShoprInvoiceId;
  if (!id) return null;
  const number = session?.repairShoprInvoiceNumber;
  return {
    label: number ? `#${number}` : 'Open in RepairShopr',
    sm4rtHref: number ? `/invoice/${number}` : null,
    rsHref: `${REPAIRSHOPR_BASE_URL}/invoices/${id}`,
    title: `RepairShopr invoice id ${id}`,
  };
}

/**
 * The session's customer, as a link into sm4rt.
 *
 * `/customer/:id` resolves **either** the Dataverse GUID or the RepairShopr
 * customer id against the cached customer index (see `CustomerDetailPage`), so
 * the RS id the session already stores addresses the page directly — no id
 * needs to be displayed to make it work.
 *
 * Null until the orchestration has matched a customer in RepairShopr; before
 * that there is no record to open.
 */
export function sessionCustomerRef(
  session: CheckInSession | null,
): { label: string; href: string } | null {
  const id = session?.repairShoprCustomerId;
  if (!id) return null;
  const label = (session?.businessName || session?.customerName || '').trim();
  return {
    label: label || `Customer #${id}`,
    href: `/customer/${encodeURIComponent(String(id))}`,
  };
}

// ---------------------------------------------------------------------------
// Aggregation helpers
// ---------------------------------------------------------------------------

export function chargesTotal(session: CheckInSession | null): number {
  if (!session) return 0;
  let sum = 0;
  for (const j of session.draftJobs ?? []) {
    for (const c of j.preliminaryCharges ?? []) {
      sum += c.finalTotal || 0;
    }
  }
  return sum;
}

export function formatAud(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'AUD' });
}

/**
 * Whether reception has dealt with payment — taken it, or chosen Defer / Not
 * Required. `not_required` alone is NOT enough: express sessions start there,
 * so it only counts once somebody chose it (`paymentOverriddenBy` is set).
 */
export function isPaymentSettled(session: CheckInSession | null): boolean {
  const state = session?.paymentState as string | undefined;
  if (state === 'not_required') return !!session?.paymentOverriddenBy;
  return SETTLED_PAYMENT_STATES.has(state ?? '');
}

const SETTLED_PAYMENT_STATES = new Set<string>([
  'paid', 'paid_overridden', 'deferred', 'deferred_awaiting_payment',
]);

/**
 * Mirrors sm4rt-api's `check_in_draft_job_service` charge lock — change them
 * together. Both deferred states lock because the customer has been sent an
 * invoice for these charges; `not_required` does not.
 */
export function isChargesLocked(session: CheckInSession | null): boolean {
  const state = session?.paymentState as string | undefined;
  return state === 'paid' || state === 'paid_overridden' || state === 'deferred'
    || state === 'deferred_awaiting_payment';
}

/**
 * Whether the session's *customer* fields are still editable.
 *
 * Deliberately stops at `intake_in_progress`: from that point the customer is
 * typing those very fields on their own phone, and a staff edit would race
 * them. Mirrors `update_session` in sm4rt-api.
 */
export function isPreLock(session: CheckInSession | null): boolean {
  const lifecycle = session?.lifecycleStatus as string | undefined;
  return lifecycle === 'draft_created' || lifecycle === 'awaiting_customer_intake';
}

/**
 * Whether the device list can still be added to, edited or removed from.
 *
 * Only a terminal session refuses. Mirrors `_TERMINAL_LIFECYCLE_STATUSES` in
 * sm4rt-api's `check_in_draft_job_service` — change them together, or the
 * button renders and the save 400s (or, as happened on session 2026100408, the
 * button vanishes while the API would have accepted the write).
 *
 * It used to freeze at `terms_accepted`, so that nothing moved under what the
 * customer agreed to. That was dropped on 2026-10-04: the terms state
 * conditions, not an inventory, and reception routinely has a second machine
 * in hand after the customer has accepted — guaranteed on the wall flow, where
 * the session is created empty and the customer accepts while staff are still
 * at the bench.
 *
 * Note this is **not** `isPreLock`, which still governs the customer-detail
 * fields: those stay locked from `intake_in_progress` because the customer is
 * typing them right now and a staff edit would race them. The device list is
 * staff-only, so there is nothing to race.
 */
export function isDeviceListEditable(session: CheckInSession | null): boolean {
  if (!session) return false;
  return !TERMINAL_LIFECYCLES.has((session.lifecycleStatus ?? '') as string);
}

/**
 * Whether reception can add a device to this session.
 *
 * Now exactly `isDeviceListEditable`. It used to be looser in one case — a
 * session with no devices could take its first one through a carve-out in the
 * lock — which then hid the button again for device 2. With the freeze gone
 * there is no carve-out and no special case; kept as its own function because
 * the call sites read better for it.
 */
export function canAddDevice(session: CheckInSession | null): boolean {
  return isDeviceListEditable(session);
}

/**
 * Whether a device can be removed from the session.
 *
 * Two conditions, and the second is the interesting one: the list must be
 * editable (i.e. not terminal), and the device must **not** already have a
 * RepairShopr ticket.
 *
 * Mirrors `remove_draft_job` in sm4rt-api's `check_in_draft_job_service` —
 * change them together, or the control renders and the delete 400s. Removing a
 * device is a soft delete of the SM4RT row that reaches nothing in
 * RepairShopr, so past ticket creation it would leave the ticket open and the
 * charges already pushed to the invoice sitting against a device nobody can
 * see. After that point it is a RepairShopr job, closed or cancelled there.
 */
export function canDeleteDevice(
  session: CheckInSession | null,
  draftJob: { repairShoprTicketId?: string | null },
): boolean {
  if (!isDeviceListEditable(session)) return false;
  return !draftJob.repairShoprTicketId;
}

/** "One job" / "Job per device" — short enough for a header chip. */
export function ticketGroupingLabel(
  grouping: CheckInTicketGrouping | string | null | undefined,
): string {
  return grouping === 'single_job' ? 'One job' : 'Job per device';
}

/**
 * Whether reception can still change the grouping.
 *
 * Mirrors the server's rule in `update_session`: changeable until any device
 * carries a RepairShopr ticket, refused afterwards — past that point the
 * grouping describes what RepairShopr already holds rather than a choice. It
 * is deliberately NOT the device-list lock: a mis-picked tile on a session
 * sitting in `awaiting_payment` has to be recoverable without cancelling the
 * check-in, which would cost an OTP-verified mobile and an accepted terms
 * snapshot too.
 *
 * Keep in step with the API, or the control renders and the save 400s.
 */
export function canEditTicketGrouping(session: CheckInSession | null): boolean {
  if (!session) return false;
  if (TERMINAL_LIFECYCLES.has((session.lifecycleStatus ?? '') as string)) return false;
  return !(session.draftJobs ?? []).some(j => j.repairShoprTicketId);
}

export const TERMINAL_LIFECYCLES = new Set<string>(['checked_in', 'expired', 'cancelled']);
